import React from 'react';

import { Box, ButtonBase } from '@mui/material';

import type { NavMode } from '@/hooks/useNavMode';
import { BORDER_RADIUS, semanticColors } from '@/theme/tokens';

interface NavModeToggleProps {
  value: NavMode;
  onChange: (mode: NavMode) => void;
  tabLabel: string;
  sheetLabel: string;
  /** Id of the card heading, so the group is named for assistive tech. */
  labelId?: string;
}

const OPTIONS: readonly NavMode[] = ['tab', 'sheet'];

/**
 * Two-option segmented control (Tab bar / Sheet) for the settings card, styled
 * after the ballot's per-election party selector: a rounded container with a
 * concentric inner radius on the halves and the selected half filled.
 *
 * Each half selects itself, as a radio group must — the party selector's
 * "click anywhere flips it" behavior reads fine on a two-state pill but is
 * wrong here, where the halves are announced as radios and activating the
 * selected one would move the choice away from it.
 */
const NavModeToggle: React.FC<NavModeToggleProps> = ({
  value,
  onChange,
  tabLabel,
  sheetLabel,
  labelId,
}) => {
  const labels: Record<NavMode, string> = { tab: tabLabel, sheet: sheetLabel };
  // Arrow keys move between radios in a group; MUI's ButtonBase does not
  // implement that for a hand-rolled group, so wire it here — otherwise a
  // keyboard user can reach the group but not change the choice within it.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    const next: NavMode = value === 'tab' ? 'sheet' : 'tab';
    onChange(next);
    // Follow the selection with focus, as a radio group does.
    (event.currentTarget.querySelector(`[data-nav-mode="${next}"]`) as HTMLElement | null)?.focus();
  };
  return (
    <Box
      role="radiogroup"
      aria-labelledby={labelId}
      onKeyDown={handleKeyDown}
      sx={{
        display: 'flex',
        // Inter-button gap softly separates the halves while the selected fill
        // still reaches the outer border; overflow: hidden clips the fills to
        // the container's rounded corners.
        gap: '3px',
        height: 34,
        borderRadius: BORDER_RADIUS,
        border: `1px solid ${semanticColors.ballotElectionCard}`,
        overflow: 'hidden',
      }}
    >
      {OPTIONS.map((mode) => {
        const selected = value === mode;
        return (
          <ButtonBase
            key={mode}
            role="radio"
            aria-checked={selected}
            aria-label={labels[mode]}
            data-nav-mode={mode}
            // Roving tabindex: the selected radio is the group's single tab
            // stop, so Tab enters the group once and the arrows move within it.
            tabIndex={selected ? 0 : -1}
            onClick={() => {
              if (!selected) onChange(mode);
            }}
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              // Concentric inner radius (outer − 1px border) so the fill's
              // corners match the container's.
              borderRadius: `calc(${BORDER_RADIUS} - 1px)`,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              fontWeight: 600,
              color: selected ? semanticColors.textOnPrimary : semanticColors.textPrimary,
              backgroundColor: selected ? semanticColors.ballotElectionCard : 'transparent',
              transition: 'background-color 0.2s ease, color 0.2s ease',
              '&:hover': {
                backgroundColor: selected
                  ? semanticColors.ballotElectionCard
                  : semanticColors.overlaySubtle,
              },
              '&:focus-visible': {
                outline: `2px solid ${semanticColors.brandAccent}`,
                outlineOffset: '-2px',
              },
            }}
          >
            {labels[mode]}
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default NavModeToggle;
